import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import HeadersManager from 'rest/HeadersManager'
import { DELETE, GET, POST } from 'rest/request'
import * as nativeApp from 'utils/nativeApp'
import { loadState } from 'utils/storage'
import { parseJwt } from 'utils/parseJwt'
import * as urls from 'rest/urls'

export const LANGUAGE_SESSION = false
export const LANGUAGE_KEY = 'lang'
export const AVAILABLE_LANGUAGES = {
  pl: 'pl',
  en: 'en'
}

export const fetchLogIn = createAsyncThunk('login/fetchLogIn',
  /**
   * @param {Object} body
   */
  async body => {
    const response = await POST(urls.login, { body, credentials: true })
    return response
  })

export const fetchPreregister = createAsyncThunk('login/fetchPreregister',
  /**
   * @param {{ phone?: string, prefix?: string, email?: string }} body
   */
  async body => {
    const response = await POST(urls.preregister, { body })
    return response
  })

export const recoverAccount = createAsyncThunk('login/recoverAccount',
  /**
   * @param {{ token: string, sms_code?: string, email_code?: string, password: string, password_confirmation: string }} body
   */
  async (body, { rejectWithValue }) => {
    try {
      return await POST(urls.accountRecovery, { body, credentials: true }, false)
    } catch (error) {
      return rejectWithValue(parseRequestError(error))
    }
  })

export const parseRequestError = (error) => {
  try {
    const data = JSON.parse(error)
    return { message: data?.message || '', code: data?.errors?.code || '' }
  } catch (_) {
    return { message: typeof error === 'string' ? error : '', code: '' }
  }
}

export const fetchLogOut = createAsyncThunk('login/fetchLogOut', async () => {
  const response = await DELETE(urls.logout)
  return response
})

export const fetchToken = createAsyncThunk('login/fetchToken', async () => {
  const response = await POST(urls.refresh, { credentials: true }, false)
  return response
})

export const getBalance = createAsyncThunk('login/getBalance', async () => {
  const response = await GET(urls.userBalance)
  return response
})

export const removeAccount = createAsyncThunk('login/removeAccount', async () => {
  const response = await DELETE(urls.user)
  return response
})

export const getFavorites = createAsyncThunk('login/getFavorites', async () => {
  const response = await GET(urls.favorites)
  return response
})

const getInitialState = (isLoading = false) => {
  return {
    isLoading,
    loggedIn: false,
    recovery: null,
    registration: null,
    expirationDate: 0,
    language: loadState(
      LANGUAGE_SESSION,
      LANGUAGE_KEY,
      navigator.language.startsWith('pl')
        ? AVAILABLE_LANGUAGES.pl
        : AVAILABLE_LANGUAGES.en
    ),
    notificationsEnabled: false,
    user: {
      name: '',
      pin: false,
      device_verification: false,
    }
  }
}

export const getMissingRequiredInfo = (data) => {
  const requiredInfo = data?.cfg?.hasRequiredInfo
  if (requiredInfo && typeof requiredInfo === 'object') {
    return requiredInfo.result === false && requiredInfo.fields
      ? requiredInfo.fields
      : null
  }
  if (data?.hasIdentificationData?.fields) {
    return data.hasIdentificationData.fields
  }
  return undefined
}

const logInUser = (state, data) => {
  if (data?.access_token && data?.cfg) {
    const { expires_in, cfg, access_token } = data
    const { name = '', sub = '' } = parseJwt(access_token)
    HeadersManager.jwt.set(access_token)
    state.expirationDate = (expires_in - 60) * 1000
    state.user = { ...cfg, name, id: sub }
    state.notificationsEnabled = !!cfg.hasPushNotification
    state.language = cfg.language || AVAILABLE_LANGUAGES.pl
    state.isLoading = false
    state.loggedIn = true
    state.error = ''
    const missingInfo = getMissingRequiredInfo(data)
    if (missingInfo !== undefined) {
      state.registration = missingInfo
    }

  } else {
    HeadersManager.jwt.destroy()
    return getInitialState()
  }
}

export const loginSlice = createSlice({
  name: 'login',
  initialState: getInitialState(true),
  reducers: {
    addPin: (state, _) => { state.user.pin = true },
    resetRegistration: (state, _) => { state.registration = null },
    resetRecovery: (state, _) => { state.recovery = null },
    setAppLoading: (state, action) => { state.isLoading = action.payload },
    updateLanguage: (state, action) => { state.language = action.payload },
    updateFavorites: (state, action) => { state.user.favorites = action.payload },
    updateShortcuts: (state, action) => { state.user.shortcuts = action.payload },
    updateUser: (state, action) => { state.user = { ...state.user, ...action.payload } },
    updateDeviceVerification: (state, action) => { state.user.device_verification = action.payload },
    updateNotification: (state, action) => { state.notificationsEnabled = action.payload },
    updateOAuth: (state, action) => {
      const data = logInUser(state, action.payload)
      if (action.payload?.access_token) {
        nativeApp.registerPushNotifications(false)
      }
      if (data) { return data }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLogIn.fulfilled, (state, action) => {
        const data = logInUser(state, action.payload)
        if (action.payload?.access_token) {
          nativeApp.registerPushNotifications(false)
        }
        if (data) { return data }
      })
      .addCase(fetchPreregister.fulfilled, (state, action) => {
        const recovery = action.payload?.recovery
        const body = action.meta.arg || {}
        const login = body.phone ? { phone: body.phone, prefix: body.prefix } : { email: body.email }
        if (recovery?.token) {
          state.recovery = {
            ...recovery,
            login,
            receivedAt: Date.now(),
          }
        } else if (state.recovery && !!state.recovery.login?.phone === !!login.phone) {
          state.recovery = null
        }
      })
      .addCase(recoverAccount.fulfilled, (state, action) => {
        const data = logInUser(state, action.payload)
        if (action.payload?.access_token) {
          nativeApp.registerPushNotifications(false)
        }
        if (data) { return data }
        state.recovery = null
      })
      .addCase(fetchLogOut.fulfilled, (state, _) => {
        if (state?.user?.hasPushNotification) {
          nativeApp.unregisterPushNotifications(false)
        }
        return logInUser(state, false)
      })
      .addCase(removeAccount.fulfilled, (state, _) => {
        if (state?.user?.hasPushNotification) {
          nativeApp.unregisterPushNotifications(false)
        }
        return logInUser(state, false)
      })
      .addCase(fetchToken.fulfilled, (state, action) => {
        const data = logInUser(state, action.payload)
        nativeApp.checkPushNotificationStatus()
        if (data) { return data }
      })
      .addCase(fetchToken.pending, (state, _) => {
        state.isLoading = true
      })
      .addCase(fetchToken.rejected, (state, _) => {
        const data = logInUser(state, false)
        nativeApp.checkPushNotificationStatus()
        if (data) { return data }
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        const { invoices, unpaidInvoicesAmount } = action.payload
        state.user.unpaidInvoicesAmount = unpaidInvoicesAmount
        state.user.unpaid_invoices = invoices
      })
      .addCase(getFavorites.fulfilled, (state, action) => {
        if (state.user && Array.isArray(action.payload?.data)) {
          state.user.favorites = action.payload.data
          state.user.showFavorites = action.payload.data.length > 0
        }
      })
  },
})

export const { addPin, resetRegistration, resetRecovery, updateLanguage, setAppLoading, updateFavorites, updateShortcuts, updateUser, updateDeviceVerification, updateNotification, updateOAuth } = loginSlice.actions

export const selectLanguage = state => state.login.language
export const selectIsLoggedIn = state => state.login.loggedIn
export const selectIsLoading = state => state.login.isLoading
export const selectExpirationDate = state => state.login.expirationDate
export const selectRegistrationData = state => state.login.registration
export const selectRecovery = state => state.login.recovery
export const selectUserNotificationsEnabled = state => state.login.notificationsEnabled
export const selectUserFavorites = state => state.login.user?.favorites || []
export const selectUserShortcuts = state => state.login.user?.shortcuts
export const selectUserId = state => state.login.user?.id
export const selectUser = state => state.login.user

export default loginSlice.reducer
